/**
 * **画布上的双指手势**：两指平移 + 捏合缩放。
 *
 * ## 为什么必须自己写
 *
 * Vue Flow（这一版）的 d3-zoom filter 是这样的（`vue-flow-core.mjs:8560`）：
 *
 * ```js
 * if (!shouldPanOnDrag && (event.type === 'mousedown' || event.type === 'touchstart')) return false
 * if (Array.isArray(panOnDrag) && !panOnDrag.includes(eventButton) && ...) return false
 * ```
 *
 * ⚠️ `eventButton` 读的是 `event.button`，而 **TouchEvent 没有 `.button`** ——
 * 它是 `undefined`。于是：
 *
 * | `panOnDrag` | 触摸能不能进 zoom 引擎 |
 * |---|---|
 * | `false` | ❌ 整条触摸链被挡 |
 * | `[0]` / `[1]` | ❌ `[n].includes(undefined)` 恒为 false |
 * | `true` | ✅ `!undefined` 为真 |
 *
 * 也就是说：**`panOnDrag` 一个值同时管"鼠标怎么拖"和"触摸能不能进"**，
 * 没有中间档。想要「单指框选 + 双指缩放」，引擎给不了 —— 只能自己接。
 *
 * ## 和引擎的分工（`.np-graph` 上的 capture 监听）
 *
 * Vue Flow 的框选起手是（`8278`）：
 *
 * ```js
 * if (... || event.target !== container.value || ...) return   // 只认落在 pane 上的按下
 * ```
 *
 * 它绑在 pane 上，而我们在它的**祖先** `.np-graph` 上用 **capture** ——
 * 捕获阶段是先祖先后目标，所以：
 *
 *   · **第一根手指**：不拦，让它正常开始框选；
 *   · **第二根手指落下**：`stopPropagation()` 拦住后续，并且把引擎
 *     已经开始的框选**取消掉**（`userSelectionActive = false`）。
 *
 * ⚠️ 这比"按设备类型猜"稳：我们**不依赖**任何媒体查询，
 * 只看"现在屏幕上有几根手指"。判错的可能性没有了。
 *
 * ## 2027：它搬到 `graph/` 了（原来在 `playground/composables/`）
 *
 * `package-boundaries.md` §9 早就把它当例子写过：
 *
 * > 「`useCanvasPinch` 这个模块就是一个现成例子：**它不依赖任何渲染库**
 * > （自己在 `.np-graph` 的 capture 阶段收 pointer 事件），
 * > 所以它天然属于 `@node-print/graph` 而不是 `editor-vue`。」
 *
 * 实测确认：它**一行都不 import 引擎**（只用 `minimapGesture` 的纯函数 +
 * `flowStore` 的视口读取器），所以这次只搬了位置、没改一行逻辑。
 *
 * ⚠️ 为什么这件事值得做：**换画布时手势层不用重写**。
 * 它读的是"指针事件 + 视口"，不是"Vue Flow 的某个 API"——
 * 于是 §6 估算里那些"已经是我们写的"部分（手势 / 小地图 / 分割条 / 长按）
 * 现在在目录上也能一眼看出来。
 */
import { pinchAround, distanceBetween, midpointOf, isZoomIntent } from './minimapGesture'
import { viewportOf } from './flowStore'
import type { FlowStoreApi } from './flowStore'

export interface CanvasPinchCtx {
  flowStore: { value: FlowStoreApi | null }
  /** Area id → 画布 DOM（量矩形要用它把屏幕坐标换成画布内坐标） */
  graphEls: Map<string, HTMLElement>
  focusedId: { value: string | null }
}

export function useCanvasPinch(ctx: CanvasPinchCtx) {
  /** 当前按在画布上的触摸点（只收 `pointerType === 'touch'`） */
  const pointers = new Map<number, { x: number; y: number }>()
  let lastMid: { x: number; y: number } | null = null
  let lastDist = 0
  /** 手势开始时的两指距离（判"想不想缩放"要用它算总变化） */
  let startDist = 0
  /** 本次手势是否已经判定为"在缩放"（判定过就锁住，见 `isZoomIntent`） */
  let zoomArmed = false

  function reset(): void {
    pointers.clear()
    lastMid = null
    lastDist = 0
    startDist = 0
    zoomArmed = false
  }

  /** 双指手势期间，引擎的框选必须停 —— 它是"单指语义" */
  function cancelEngineSelection(store: FlowStoreApi | null): void {
    /*
     * ⚠️ 可写：`userSelectionActive` 是 store 上的 ref（`store.d.ts:74`，
     * 由 `useVueFlow()` 暴露）。置 false 会把引擎那层选择框收掉。
     * 拿不到就跳过 —— 不要为了它抛错（不同小版本不一定有）。
     */
    const flag = (store as unknown as { userSelectionActive?: { value: boolean } } | null)
      ?.userSelectionActive
    if (flag) flag.value = false
  }

  function onPointerDown(ev: PointerEvent): void {
    if (ev.pointerType !== 'touch') return
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })

    if (pointers.size === 2) {
      /* ★ 第二根手指 = 双指手势，把引擎已经开始的框选撤掉 */
      cancelEngineSelection(ctx.flowStore.value)
      const [a, b] = [...pointers.values()]
      lastMid = midpointOf(a!, b!)
      lastDist = distanceBetween(a!, b!)
      startDist = lastDist
      zoomArmed = false
    }

    /* 两根以上：从这一刻起不让引擎再看到事件 */
    if (pointers.size >= 2) {
      ev.stopPropagation()
      ev.preventDefault?.()
    }
  }

  function onPointerMove(ev: PointerEvent): void {
    if (!pointers.has(ev.pointerId)) return
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    if (pointers.size < 2) return

    ev.stopPropagation()

    const store = ctx.flowStore.value
    if (!store || !lastMid) return

    const [a, b] = [...pointers.values()]
    const mid = midpointOf(a!, b!)
    const dist = distanceBetween(a!, b!)
    if (lastDist <= 0) {
      lastMid = mid
      lastDist = dist
      return
    }

    const rect = ctx.graphEls.get(ctx.focusedId.value ?? '')?.getBoundingClientRect()
    const vp = viewportOf(store)

    /* ① 先按**中点位移**平移（两指一起挪 = 拖画布） */
    const panned = { x: vp.x + (mid.x - lastMid.x), y: vp.y + (mid.y - lastMid.y), zoom: vp.zoom }

    /* ② 再围绕中点缩放。锚点要**画布坐标** —— 先减容器原点 */
    const local = rect ? { x: mid.x - rect.left, y: mid.y - rect.top } : mid
    const anchor = { x: (local.x - panned.x) / panned.zoom, y: (local.y - panned.y) / panned.zoom }

    /*
     * ★ **先判意图，再决定要不要缩放。**
     *
     * 两指手势天然同时产生位移和距离变化 —— 想平移时距离也会抖，
     * 于是画面一边挪一边轻微缩放（实测：「双指拖和双指缩放有点重叠」）。
     * 距离没超过阈值就只平移：`factor` 给 1，`pinchAround` 会原样返回。
     */
    zoomArmed = isZoomIntent(startDist, dist, zoomArmed)
    const factor = zoomArmed ? dist / lastDist : 1

    store.setViewport(pinchAround(panned, factor, anchor))

    lastMid = mid
    lastDist = dist
  }

  function onPointerUp(ev: PointerEvent): void {
    pointers.delete(ev.pointerId)
    /* 松到只剩一根（或零根）就结束这次双指手势 —— 剩下的那根不再接管 */
    if (pointers.size < 2) {
      lastMid = null
      lastDist = 0
      startDist = 0
      zoomArmed = false
    }
  }

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    /**
     * ⚠️ `pointercancel` 要**清空全部**：系统手势（下拉通知栏、切应用）
     * 取消指针序列时 `pointerup` 不会来，不清的话 `pointers` 里会留着
     * 幽灵手指，之后**每次单击都被当成双指**。
     */
    onPointerCancel: reset,
    /** 当前是不是双指手势（宿主用它决定要不要继续做长按判定） */
    isPinching: () => pointers.size >= 2,
  }
}
